import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { updateMe } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { ArrowRight, GraduationCap, Building2, Briefcase, School, Loader2 } from "lucide-react";

export default function RoleChooser() {
  const navigate = useNavigate();
  const { refresh } = useAuth();
  const [busy, setBusy] = useState(null);
  const [error, setError] = useState(null);

  const pick = async (role) => {
    setBusy(role);
    setError(null);
    try {
      // Agency / college: skip the user role-patch (workspace creation sets the role).
      if (role === "agency") {
        navigate("/agency/onboarding", { replace: true });
        return;
      }
      if (role === "college") {
        navigate("/campus/onboarding", { replace: true });
        return;
      }
      await updateMe({
        role,
        onboarding_step: role === "candidate" ? "profile" : "company",
      });
      await refresh();
      if (role === "candidate") navigate("/onboarding/profile", { replace: true });
      else navigate("/company/onboarding", { replace: true });
    } catch (e) {
      setError(e?.response?.data?.detail || "Could not save your choice");
      setBusy(null);
    }
  };

  return (
    <div className="min-h-screen bg-[#FDFBF7] wsh-grain flex flex-col">
      <div className="container-x pt-10">
        <span className="font-display text-2xl font-bold inline-flex items-baseline gap-1">
          Wishire
          <span className="font-display italic text-xs text-stone-500">.ai</span>
          <span className="ml-1 w-1.5 h-1.5 rounded-full bg-[#C2410C] inline-block translate-y-[-2px]" />
        </span>
      </div>

      <div className="flex-1 flex items-center">
        <div className="container-x max-w-5xl">
          <span className="overline">Welcome to Wishire</span>
          <h1 className="font-display text-4xl md:text-6xl leading-[1.05] mt-4">
            How will you{" "}
            <span className="italic text-stone-500">use Wishire?</span>
          </h1>
          <p className="mt-5 text-stone-600 max-w-2xl text-lg leading-relaxed">
            Pick once — we&apos;ll set up the right workspace for you. You can&apos;t
            switch later, so choose carefully.
          </p>

          <div className="mt-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <Card
              testId="role-pick-candidate"
              busy={busy === "candidate"}
              onClick={() => pick("candidate")}
              icon={GraduationCap}
              eyebrow="Candidate"
              title="I&rsquo;m looking for my next role."
              body="Build your AI resume, run voice mock interviews, earn your Wishire Score, and apply with proof."
              accent="#C2410C"
              tone="light"
            />
            <Card
              testId="role-pick-company"
              busy={busy === "company"}
              onClick={() => pick("company")}
              icon={Building2}
              eyebrow="Company"
              title="We&rsquo;re hiring."
              body="Post roles, see every applicant pre-ranked by Wishire Score, and screen with AI before they apply."
              accent="#FCD34D"
              tone="dark"
            />
            <Card
              testId="role-pick-agency"
              busy={busy === "agency"}
              onClick={() => pick("agency")}
              icon={Briefcase}
              eyebrow="Recruiting agency"
              title="We place candidates for clients."
              body="Manage multiple clients, source candidates ranked by score, and submit them with objective proof of quality."
              accent="#C2410C"
              tone="light"
            />
            <Card
              testId="role-pick-college"
              busy={busy === "college"}
              onClick={() => pick("college")}
              icon={School}
              eyebrow="College / placement cell"
              title="We place our students."
              body="Track your batch's placement readiness, run campus drives with eligibility rules, and shortlist students by an objective score."
              accent="#15803D"
              tone="light"
            />
          </div>

          {error && <p className="mt-5 text-red-700 text-sm" data-testid="role-error">{error}</p>}
        </div>
      </div>
    </div>
  );
}

function Card({ testId, busy, onClick, icon: Icon, eyebrow, title, body, accent, tone }) {
  const dark = tone === "dark";
  return (
    <button
      type="button"
      data-testid={testId}
      onClick={onClick}
      disabled={busy}
      className={`relative text-left rounded-[2rem] p-8 md:p-10 transition-all duration-300 hover:-translate-y-1 disabled:opacity-60 ${
        dark
          ? "bg-stone-900 text-stone-50 hover:shadow-[0_24px_60px_-20px_rgba(28,25,23,0.4)]"
          : "bg-white border border-stone-200 hover:shadow-[0_24px_60px_-20px_rgba(28,25,23,0.2)]"
      } wsh-grain overflow-hidden`}
    >
      {dark && (
        <div
          className="absolute inset-0 opacity-40 pointer-events-none"
          style={{
            background:
              "radial-gradient(500px 320px at 80% 20%, rgba(217,119,6,0.4), transparent 60%)",
          }}
        />
      )}
      <div className="relative">
        <div className={`inline-flex p-3 rounded-2xl ${dark ? "bg-white/10 text-amber-300" : "bg-stone-100"}`} style={dark ? {} : { color: accent }}>
          <Icon size={22} />
        </div>
        <p className="overline mt-6" style={dark ? { color: "rgba(253,251,247,0.55)" } : {}}>
          {eyebrow}
        </p>
        <h3
          className="font-display text-3xl md:text-4xl mt-2 leading-tight"
          dangerouslySetInnerHTML={{ __html: title }}
        />
        <p className={`mt-4 leading-relaxed ${dark ? "text-stone-300" : "text-stone-600"}`}>{body}</p>
        <div className="mt-8 inline-flex items-center gap-2 font-medium" style={{ color: dark ? "#FCD34D" : "#C2410C" }}>
          {busy ? (
            <>
              <Loader2 size={16} className="animate-spin" /> Saving
            </>
          ) : (
            <>
              Continue <ArrowRight size={16} />
            </>
          )}
        </div>
      </div>
    </button>
  );
}
